import React from 'react'
import { PixelDate, PixelTable, DcTag, colors } from 'pixel-base'

import { type SalesHistoryItem } from 'utils/api-routes-planning'

interface SalesTableProps {
  data: SalesHistoryItem[]
  onRowClick?: (row: SalesHistoryItem) => void
  isLoading?: boolean
}

const SalesTable: React.FC<SalesTableProps> = ({
  data,
  onRowClick,
  isLoading,
}) => {
  const getChannelColor = (channel: string) => {
    const channelLower = channel?.toLowerCase() || ''
    if (channelLower.includes('e-commerce')) {
      return { bg: colors.primaryLight, text: colors.primary }
    } else if (channelLower.includes('distribution')) {
      return { bg: colors.secondaryLight, text: colors.secondary }
    } else if (channelLower.includes('retail')) {
      return { bg: colors.warningLight, text: colors.warning }
    }
    return { bg: colors.grayLight, text: colors.grayText }
  }

  const getMonthName = (month: number): string => {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    return months[month - 1] || '--'
  }

  const columns = [
    {
      dataKey: 'created_at',
      name: 'Creation Date',
      sortable: true,
      selector: (row: SalesHistoryItem) =>
        row.created_at ? (
          <PixelDate
            value={row.created_at.date}
            format="dateWithTime"
            className={''}
          />
        ) : (
          '--'
        ),
    },
    {
      dataKey: 'month',
      name: 'Sale Month',
      sortable: true,
      selector: (row: SalesHistoryItem) => {
        const monthName = getMonthName(row.sale_month)
        return (
          <DcTag
            customBackgroundColor={colors.infoLight}
            textColor={colors.info}
            variant="small"
          >
            {monthName}
          </DcTag>
        )
      },
    },
    {
      dataKey: 'year',
      name: 'Sale Year',
      sortable: true,
      selector: (row: SalesHistoryItem) => {
        const year = row.sale_year || '--'
        return (
          <DcTag
            customBackgroundColor={colors.successLight}
            textColor={colors.success}
            variant="small"
          >
            {year}
          </DcTag>
        )
      },
    },
    {
      dataKey: 'total_products',
      name: 'Total Products',
      sortable: true,
      selector: (row: SalesHistoryItem) =>
        typeof row.total_products === 'number' ? row.total_products : '--',
    },
    {
      dataKey: 'total_qty_sold',
      name: 'Total Qty Sold',
      sortable: true,
      selector: (row: SalesHistoryItem) =>
        typeof row.total_qty_sold === 'number' ? row.total_qty_sold : '--',
    },
    {
      dataKey: 'growth_factor',
      name: 'Growth Factor',
      sortable: true,
      selector: (row: SalesHistoryItem) =>
        typeof row.growth_factor === 'number' ? row.growth_factor : '--',
    },
    {
      dataKey: 'sale_channel',
      name: 'Sale Channel',
      sortable: true,
      selector: (row: SalesHistoryItem) => {
        const channel = row.sale_channel || '--'
        const channelColors = getChannelColor(channel)
        return (
          <DcTag
            customBackgroundColor={channelColors.bg}
            textColor={channelColors.text}
            variant="small"
          >
            {channel.toUpperCase()}
          </DcTag>
        )
      },
    },
  ]

  return (
    <>
      <PixelTable
        columns={columns}
        data={data || []}
        isSearchable={true}
        pointerOnHover
        onRowClicked={onRowClick}
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 320px)"
        progressPending={isLoading}
        pagination={data.length > 25}
        paginationPerPage={25}
      />
    </>
  )
}

export default SalesTable
